import {
  Skill,
  matchSkill,
  rankSkills,
  recentSkillIds,
  setSkillFavorite
} from '@/lib/skills';
import { cn } from '@/lib/utils';
import {
  ArrowLeftRight,
  Building2,
  ClipboardList,
  Clock,
  HardHat,
  Landmark,
  LayoutGrid,
  Package,
  ScrollText,
  Search,
  ShoppingCart,
  Star,
  Truck
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog';

export const SKILL_ICONS: Record<string, typeof ScrollText> = {
  account_statement: ScrollText,
  stock_card: Package,
  warehouse_transfers: ArrowLeftRight
};

const GROUP_ICONS: Record<string, typeof ScrollText> = {
  Finance: Landmark,
  Inventory: Package,
  Sales: ShoppingCart,
  Purchasing: Truck,
  'Real Estate': Building2,
  Projects: HardHat
};

/** A skill's own icon, else its group's. */
export const skillIcon = (s: Skill) =>
  SKILL_ICONS[s.id] || (s.group && GROUP_ICONS[s.group]) || ClipboardList;

const FAVOURITES = 'Favourites';
const RECENT = 'Recent';
const ALL = 'All';
// Recent skills shown above the groups in "All"; the full list is its own view.
const RECENT_ON_TOP = 4;
// The side sections before the groups; a divider follows them.
const FIXED_SECTIONS = 3;

interface Props {
  open: boolean;
  skills: Skill[];
  onClose: () => void;
  onPick: (skill: Skill) => void;
}

type Section = { title: string; rows: Skill[] };

/** Cards per row, read from the breakpoint the grid uses. */
const columns = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(min-width: 640px)').matches
    ? 2
    : 1;

/** A phone or tablet: a coarse pointer and no hover. */
const touchScreen = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(pointer: coarse)').matches;

/**
 * Every skill this user has, in a two-pane window: sections down the side
 * (everything, favourites, recent, then one per group), the skills as cards
 * on the right. A section shows all its skills and the pane scrolls. On a
 * phone the side becomes a row of chips above one column of cards.
 */
export const SkillsPalette = ({ open, skills, onClose, onPick }: Props) => {
  const [q, setQ] = useState('');
  const [view, setView] = useState(ALL);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setActive(0);
    setView(ALL);
  }, [open]);

  const groups = useMemo(
    () => [...new Set(skills.map((s) => s.group || 'Other'))],
    [skills]
  );
  const recent = useMemo(
    () =>
      open
        ? (recentSkillIds()
            .map((id) => skills.find((s) => s.id === id))
            .filter(Boolean) as Skill[])
        : [],
    [skills, open]
  );
  const favs = useMemo(() => skills.filter((s) => s.favorite), [skills]);

  const sections: Section[] = useMemo(() => {
    if (q.trim()) {
      // Searching looks everywhere, best match first, whatever the section.
      const ranked = rankSkills(
        skills.filter((s) => matchSkill(s, q)),
        q
      );
      return ranked.length ? [{ title: 'Results', rows: ranked }] : [];
    }
    if (view === FAVOURITES)
      return favs.length ? [{ title: FAVOURITES, rows: favs }] : [];
    if (view === RECENT)
      return recent.length ? [{ title: RECENT, rows: recent }] : [];
    const out: Section[] = [];
    if (view === ALL && recent.length)
      out.push({ title: RECENT, rows: recent.slice(0, RECENT_ON_TOP) });
    for (const g of groups) {
      if (view !== ALL && view !== g) continue;
      const rows = skills.filter((s) => (s.group || 'Other') === g);
      if (rows.length) out.push({ title: g, rows });
    }
    return out;
  }, [skills, q, view, groups, recent, favs]);

  const flat = sections.flatMap((s) => s.rows);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKey = (e: React.KeyboardEvent) => {
    // Left and right move between cards only while the search box is empty,
    // so they still move the caret in a typed word.
    const step: Record<string, number> = {
      ArrowDown: columns(),
      ArrowUp: -columns(),
      ...(q ? {} : { ArrowRight: 1, ArrowLeft: -1 })
    };
    if (e.key in step) {
      setActive((i) =>
        Math.min(Math.max(i + step[e.key], 0), Math.max(flat.length - 1, 0))
      );
      e.preventDefault();
    } else if (e.key === 'Enter' && flat[active]) {
      onPick(flat[active]);
      e.preventDefault();
    }
  };

  const choose = (name: string) => {
    setView(name);
    setQ('');
    setActive(0);
    listRef.current?.scrollTo({ top: 0 });
    inputRef.current?.focus();
  };

  const nav: { name: string; Icon: typeof ScrollText; count: number }[] = [
    { name: ALL, Icon: LayoutGrid, count: skills.length },
    { name: FAVOURITES, Icon: Star, count: favs.length },
    { name: RECENT, Icon: Clock, count: recent.length },
    ...groups.map((g) => ({
      name: g,
      Icon: GROUP_ICONS[g] || ClipboardList,
      count: skills.filter((s) => (s.group || 'Other') === g).length
    }))
  ];
  const current = q.trim() ? '' : view;

  let index = -1;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        // Phone: the whole screen, sized to what is actually visible (dvh -
        // vh counts the space under the browser's toolbars, which hid the
        // bottom of the list). Desktop: the two-pane window.
        className="w-full max-w-none h-[100dvh] rounded-none border-0 sm:border sm:max-w-[880px] sm:h-[min(640px,88vh)] sm:rounded-lg p-0 gap-0 overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => {
          // A phone opening the keyboard on its own takes half the screen
          // from the list; there the search is focused when tapped.
          if (touchScreen()) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">All skills</DialogTitle>
        <DialogDescription className="sr-only">
          Search and run a ready-made report from your ERP data
        </DialogDescription>
        <div className="flex items-center gap-2 px-4 pr-12 border-b h-14 flex-none">
          <Search className="!size-4 text-muted-foreground flex-none" />
          <input
            ref={inputRef}
            autoFocus={!touchScreen()}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
            placeholder={`Search ${skills.length} skills in English or Arabic`}
            className="flex-1 bg-transparent text-base sm:text-[15px] outline-none"
            dir="auto"
          />
        </div>
        {/* Phone: the sections as a row of chips */}
        <div className="sm:hidden flex gap-1.5 px-3 py-2 border-b overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x flex-none">
          {nav.map(({ name, count }) => (
            <button
              key={name}
              type="button"
              onClick={() => choose(name)}
              className={cn(
                'h-7 px-2.5 rounded-full text-[12.5px] border whitespace-nowrap inline-flex items-center gap-1',
                current === name
                  ? 'bg-primary/10 border-primary/40 text-primary'
                  : 'border-border text-muted-foreground'
              )}
            >
              {name}
              <span className="opacity-70">{count}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-1 min-h-0">
          <nav className="hidden sm:flex flex-col gap-0.5 w-[200px] flex-none border-r p-2 overflow-y-auto">
            {nav.map(({ name, Icon, count }, i) => (
              <div key={name}>
                {i === FIXED_SECTIONS && <div className="border-t my-2 mx-2" />}
                <button
                  type="button"
                  onClick={() => choose(name)}
                  className={cn(
                    'w-full flex items-center gap-2.5 rounded-md px-2.5 h-9 text-sm transition-colors',
                    current === name
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                  )}
                >
                  <Icon className="!size-4 flex-none" />
                  <span className="flex-1 text-left truncate">{name}</span>
                  <span className="text-xs tabular-nums opacity-70">
                    {count}
                  </span>
                </button>
              </div>
            ))}
          </nav>
          <div
            ref={listRef}
            role="listbox"
            className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-3 sm:px-4 pb-4"
          >
            {sections.length === 0 && (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {q
                  ? 'No skill matches. Try a shorter word.'
                  : view === FAVOURITES
                    ? 'Star a skill to keep it here.'
                    : 'The skills you run show up here.'}
              </div>
            )}
            {sections.map((section) => (
              <section key={section.title}>
                <div className="flex items-center justify-between pt-4 pb-2 text-xs font-medium text-muted-foreground">
                  <span>{section.title}</span>
                  <span className="tabular-nums">{section.rows.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {section.rows.map((s) => {
                    index += 1;
                    const i = index;
                    const Icon = skillIcon(s);
                    return (
                      <div
                        key={`${section.title}-${s.id}`}
                        role="option"
                        data-index={i}
                        aria-selected={i === active}
                        onMouseMove={() => setActive(i)}
                        onClick={() => onPick(s)}
                        className={cn(
                          'group flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                          i === active
                            ? 'bg-muted border-primary/30'
                            : 'border-border hover:bg-muted/50'
                        )}
                      >
                        <span className="flex-none mt-0.5 h-8 w-8 rounded-md bg-muted inline-flex items-center justify-center">
                          <Icon className="!size-4 text-muted-foreground" />
                        </span>
                        <span className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-medium truncate">
                            {s.label}
                          </span>
                          {s.label_ar && (
                            <span
                              className="text-xs text-muted-foreground truncate text-left"
                              dir="rtl"
                            >
                              {s.label_ar}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {s.description}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={
                            s.favorite ? `Unpin ${s.label}` : `Pin ${s.label}`
                          }
                          title={
                            s.favorite
                              ? 'Remove from favourites'
                              : 'Add to favourites'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setSkillFavorite(s.id, !s.favorite);
                          }}
                          className={cn(
                            'flex-none -mr-1 -mt-0.5 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-background',
                            s.favorite
                              ? 'text-amber-500'
                              : 'text-muted-foreground opacity-40 group-hover:opacity-100'
                          )}
                        >
                          <Star
                            className="!size-4"
                            fill={s.favorite ? 'currentColor' : 'none'}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
        <div className="hidden sm:flex items-center justify-between px-4 h-9 border-t text-[11.5px] text-muted-foreground flex-none">
          <span>Arrows to move · Enter to open · Esc to close</span>
          <span>Type / in the message box to open this</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SkillsPalette;
